import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { services, faqs } from '../data/mock';
import PricingSection from '../components/PricingSection';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import * as Icons from 'lucide-react';

const ServicePage = ({ onOpenPopup }) => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);
  const [benefitIndex, setBenefitIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);

  useEffect(() => {
    if (service && service.benefits) {
      const interval = setInterval(() => {
        setBenefitIndex((prev) => (prev + 1) % service.benefits.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [service]);

  const handleDragStart = (e) => {
    setIsDragging(true);
    setStartX(e.type === 'mousedown' ? e.clientX : e.touches[0].clientX);
    setCurrentX(e.type === 'mousedown' ? e.clientX : e.touches[0].clientX);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const x = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
    setCurrentX(x);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    const diff = startX - currentX;
    const threshold = 50;

    if (diff > threshold && service.benefits) {
      setBenefitIndex((prev) => (prev + 1) % service.benefits.length);
    } else if (diff < -threshold && service.benefits) {
      setBenefitIndex((prev) => (prev - 1 + service.benefits.length) % service.benefits.length);
    }

    setIsDragging(false);
  };

  if (!service) {
    return <div className="pt-32 text-center">Service not found</div>;
  }

  const Icon = Icons[service.icon];
  const showPricing = slug === 'trademark-registration';

  // Generate meta description based on service
  const getMetaDescription = () => {
    const descriptions = {
      'trademark-registration': 'Professional trademark registration services. Protect your brand with expert USPTO filing. 98% success rate, fast processing, comprehensive protection. Get started today!',
      'copyright-registration': 'Secure copyright protection for your creative works. Music, art, writing, software registration. Expert filing with U.S. Copyright Office. Protect your creativity now!',
      'trademark-search': 'Comprehensive trademark search services. Identify potential conflicts before filing. Detailed analysis, risk assessment, professional recommendations. Save time and money!',
      'company-formation': 'Professional business entity formation services. LLC, Corporation, Partnership registration. Expert guidance, complete state filing, liability protection. Start your business right!',
      'amazon-brand-registry': 'Amazon Brand Registry enrollment services. Protect your brand on Amazon marketplace. Counterfeit protection, enhanced content, brand analytics. Secure your e-commerce presence!'
    };
    return descriptions[slug] || service.description;
  };

  return (
    <>
      <Helmet>
        <title>{service.title} - Trademark Prime Unit | Expert {service.category.charAt(0).toUpperCase() + service.category.slice(1)} Services</title>
        <meta name="description" content={getMetaDescription()} />
        <meta name="keywords" content={`${service.title.toLowerCase()}, ${service.category}, intellectual property, brand protection, legal services`} />
        <meta property="og:title" content={`${service.title} - Trademark Prime Unit`} />
        <meta property="og:description" content={service.description} />
      </Helmet>
      
      <div className="min-h-screen">
      {/* Hero Section with Service-Specific Background */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${service.hero_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900/95 via-primary-800/90 to-primary-700/95"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl inline-flex border border-white/20">
                  <Icon className="h-12 w-12 text-yellow-400" />
                </div>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight">
                {service.title}
              </h1>
              <p className="text-xl text-secondary-100 leading-relaxed">
                {service.description}
              </p>
              <Button
                onClick={onOpenPopup}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 group border-2 border-white/20"
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div>
              <img
                src={service.detail_image}
                alt={service.title}
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover ring-4 ring-white/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Information Section with Service-Specific Image */}
      <section className="pt-20 pb-4 md:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={service.hero_image}
                alt={`${service.title} details`}
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Comprehensive {service.title} Services
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our expert team provides end-to-end {service.title.toLowerCase()} services tailored to your specific needs. We ensure a smooth, efficient process from start to finish, protecting your intellectual property with precision and care.
              </p>
              <div className="space-y-3">
                {service.process && service.process.slice(0, 3).map((step, index) => {
                  const stepIcon = index === 0 ? 'CheckCircle' : index === 1 ? 'FileText' : 'Shield';
                  const StepIcon = Icons[stepIcon];
                  return (
                    <div key={index} className="flex items-center space-x-3">
                      <StepIcon className="h-6 w-6 text-primary flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{step.title}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section - Service Specific */}
      <section className="pt-20 pb-4 md:pb-20 bg-gradient-to-b from-secondary-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Our {service.title} Service?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Benefit from our expertise and commitment to protecting your {service.category === 'trademark' ? 'brand' : service.category === 'copyright' ? 'creative works' : 'business'}
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.benefits && service.benefits.map((benefit, index) => {
              const BenefitIcon = Icons[benefit.icon];
              return (
                <div key={index} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center mb-4">
                    <BenefitIcon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>

          {/* Mobile Slider */}
          {service.benefits && (
            <div className="md:hidden relative">
              <div 
                className="overflow-hidden cursor-grab active:cursor-grabbing"
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
              >
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${benefitIndex * 100}%)` }}
                >
                  {service.benefits.map((benefit, index) => {
                    const BenefitIcon = Icons[benefit.icon];
                    return (
                      <div key={index} className="w-full flex-shrink-0 px-4">
                        <div className="bg-white p-6 rounded-2xl shadow-lg">
                          <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center mb-4">
                            <BenefitIcon className="h-7 w-7 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                          <p className="text-gray-600">{benefit.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Navigation Dots */}
              <div className="flex justify-center mt-6 space-x-2">
                {service.benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setBenefitIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      benefitIndex === index ? 'bg-primary w-8' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Pricing Section - Only for Trademark Registration */}
      {showPricing && <PricingSection onOpenPopup={onOpenPopup} />}

      {/* Process/How It Works Section - Service Specific */}
      <section className="pt-20 pb-4 md:pb-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Our streamlined process makes {service.title.toLowerCase()} simple and efficient
            </p>
          </div>

          <div className="space-y-8">
            {service.process && service.process.map((step, index) => (
              <div key={index} className="flex items-start space-x-6">
                <div className="flex-shrink-0 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {index + 1}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pt-20 pb-4 md:pb-20 bg-gradient-to-b from-secondary-50/30 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Find answers to common questions about {service.title.toLowerCase()}
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={`item-${faq.id}`}
                className="bg-white rounded-xl shadow-md overflow-hidden border-none"
              >
                <AccordionTrigger className="px-6 py-4 hover:bg-secondary-100/30 transition-colors text-left">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pt-20 pb-4 md:pb-20 bg-gradient-to-br from-primary-600 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-secondary-100 mb-8">
            Protect your {service.category === 'trademark' ? 'brand' : service.category === 'copyright' ? 'creative works' : 'business'} with our professional {service.title.toLowerCase()} services
          </p>
          <Button
            onClick={onOpenPopup}
            size="lg"
            className="bg-white text-primary-800 hover:bg-secondary-50 px-12 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300"
          >
            Start Your Application
          </Button>
        </div>
      </section>
    </div>
    </>
  );
};

export default ServicePage;
